import { formatDate } from '@angular/common';
import { Pipe, PipeTransform, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { DISPLAY_DATE_FORMAT, resolveDisplayDateFormat } from 'ish-core/utils/date-format';

/**
 * Helper function to robustly parse various date formats to a Date object.
 * Supports numbers (epoch ms), ISO strings, 'YYYY,MM,DD', and
 * 'dd MMM yyyy HH:mm:ss GMT/UTC' patterns.
 */
function parseISHDate(value: Date | number | string): Date | null {
  // If already a Date instance, return as is.
  if (value instanceof Date) return value;
  // If a number, treat as milliseconds since epoch.
  if (typeof value === 'number') return new Date(value);
  if (typeof value === 'string') {
    // Try to parse as ISO 8601 string or timestamp string.
    let date = new Date(value);
    if (!isNaN(date.getTime())) return date;
    // Handle 'YYYY,MM,DD' numeric string format.
    const ymd = /^(\d{4}),(\d{1,2}),(\d{1,2})$/.exec(value);
    if (ymd) return new Date(+ymd[1], +ymd[2] - 1, +ymd[3]);
    // Handle legacy 'dd MMM yyyy HH:mm:ss GMT/UTC' format.
    const regex = /^(\d{2}) (\w{3}) (\d{4}) (\d{2}):(\d{2}):(\d{2}) (?:UTC|GMT)$/;
    const match = value.replace('GMT', 'UTC').match(regex);
    if (match) {
      // Map month string to zero-based number (e.g., 'Jan' => 0).
      const month = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].indexOf(
        match[2]
      );
      return new Date(Date.UTC(+match[3], month, +match[1], +match[4], +match[5], +match[6]));
    }
  }
  // Return null if unable to parse.
  return null;
}

/**
 * Formats any supported date input as a localized date string.
 * Falls back to '-' if the input is invalid.
 *
 * CUSTOMIZATION: Calendar dates display as MM-dd-yyyy (US) site-wide.
 *
 * @param value - Supported input: Date, epoch ms, or string formats
 * @param format - Angular date format (default: MM-dd-yyyy)
 * @param lang - Locale/language code for output format
 * @param timezone - Optional timezone for output
 */
export function formatISHDate(
  value: Date | number | string,
  format = DISPLAY_DATE_FORMAT,
  lang: string,
  timezone?: string
): string {
  const date = parseISHDate(value);
  if (!date) {
    return '-';
  }
  const resolved = resolveDisplayDateFormat(format);
  const locale = lang?.includes('_') ? lang.replace(/_/g, '-') : lang || 'en-US';
  return formatDate(date, resolved, locale, timezone);
}

/**
 * Angular Pipe: Converts number, string or Date value
 * to a localized date string using TranslateService's current language.
 * Usage:
 *   {{ myDateValue | ishDate:'longDate':'Asia/Kolkata' }}
 */
@Pipe({
  name: 'ishDate',
  pure: true,
})
export class DatePipe implements PipeTransform {
  // Inject current TranslateService for language detection.
  private trans = inject(TranslateService);

  transform(value: Date | number | string, format = DISPLAY_DATE_FORMAT, timezone?: string): string {
    return formatISHDate(value, format, this.trans.getCurrentLang(), timezone);
  }
}
